import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import ReactDOM from "react-dom";
import { alpha, makeStyles, useTheme } from "@material-ui/core/styles";
import {
  Button,
  Chip,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from "@material-ui/core";
import PauseIcon from "@material-ui/icons/Pause";
import PlayArrowIcon from "@material-ui/icons/PlayArrow";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import DragIndicatorIcon from "@material-ui/icons/DragIndicator";
import CenterFocusStrongIcon from "@material-ui/icons/CenterFocusStrong";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { AuthContext } from "../../context/Auth/AuthContext";
import api from "../../services/api";
import toastError from "../../errors/toastError";

const STORAGE_KEY = "operationalTaskWidgetPositions-v3";
const UPDATE_EVENT = "operational-task-widget-sync";
const MIN_VISIBLE_WIDGET_SLICE = 72;
const MOBILE_MIN_VISIBLE_WIDGET_SLICE = 148;
const MIN_WIDGET_MARGIN = 8;
const DESKTOP_TOP_SAFE_MARGIN = 16;
const MOBILE_TOP_SAFE_MARGIN = 156;
const ESTIMATED_WIDGET_WIDTH = 290;
const ESTIMATED_WIDGET_HEIGHT = 190;

const useStyles = makeStyles(theme => ({
  container: {
    position: "fixed",
    inset: 0,
    pointerEvents: "none",
    zIndex: 1600
  },
  widget: {
    position: "fixed",
    width: 290,
    maxWidth: "calc(100vw - 24px)",
    borderRadius: 18,
    padding: theme.spacing(1.25, 1.25, 1.1),
    boxShadow: `0 20px 40px ${alpha("#0f172a", 0.26)}`,
    border: `1px solid ${alpha(theme.palette.primary.main, 0.16)}`,
    background: theme.palette.type === "dark"
      ? `linear-gradient(180deg, ${alpha("#182533", 0.98)} 0%, ${alpha("#101a24", 0.98)} 100%)`
      : "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
    pointerEvents: "auto",
    userSelect: "none",
    touchAction: "none"
  },
  header: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1)
  },
  dragArea: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    cursor: "grab",
    minWidth: 0,
    flex: 1,
    padding: theme.spacing(0.5, 0),
    touchAction: "none",
    "&:active": {
      cursor: "grabbing"
    }
  },
  titleWrap: {
    minWidth: 0
  },
  title: {
    fontWeight: 800,
    lineHeight: 1.2,
    overflow: "hidden",
    textOverflow: "ellipsis",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical"
  },
  subtitle: {
    marginTop: 2
  },
  timerBox: {
    borderRadius: 14,
    padding: theme.spacing(1.1),
    textAlign: "center",
    background: theme.palette.type === "dark"
      ? alpha("#0f172a", 0.76)
      : alpha(theme.palette.primary.main, 0.05),
    border: `1px solid ${alpha(theme.palette.primary.main, 0.14)}`
  },
  timerValue: {
    fontWeight: 900,
    letterSpacing: "0.08em",
    fontFamily: "Consolas, 'Courier New', monospace"
  },
  metaRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    marginTop: theme.spacing(1)
  },
  footerActions: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(1.2)
  },
  actionButton: {
    flex: 1,
    fontWeight: 700,
    borderRadius: 12
  },
  primaryDone: {
    backgroundColor: "#0f9d58",
    color: "#fff",
    "&:hover": {
      backgroundColor: "#0b7d45"
    }
  }
}));

const formatDuration = totalSeconds => {
  const safe = Math.max(0, Number(totalSeconds || 0));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  return `${String(hours).padStart(2, "0")}:${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
};

const readStoredPositions = () => {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}");
  } catch (_error) {
    return {};
  }
};

const persistStoredPositions = positions => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(positions));
};

const computeDefaultPosition = index => {
  const baseLeft = Math.max(
    MIN_WIDGET_MARGIN,
    window.innerWidth - ESTIMATED_WIDGET_WIDTH - 16
  );
  const baseTop = Math.max(
    getTopSafeMargin(),
    window.innerHeight - ESTIMATED_WIDGET_HEIGHT - 24
  );
  const column = Math.floor(index / 3);
  const row = index % 3;

  return {
    left: Math.max(MIN_WIDGET_MARGIN, baseLeft - column * 306),
    top: Math.max(getTopSafeMargin(), baseTop - row * 182)
  };
};

const getVisibleWidgetSlice = () => (
  window.innerWidth <= 768 ? MOBILE_MIN_VISIBLE_WIDGET_SLICE : MIN_VISIBLE_WIDGET_SLICE
);

const getTopSafeMargin = () => (
  window.innerWidth <= 768 ? MOBILE_TOP_SAFE_MARGIN : DESKTOP_TOP_SAFE_MARGIN
);

const isMobileViewport = () => window.innerWidth <= 768;

const getViewportMetrics = () => {
  const viewport = window.visualViewport;

  if (viewport) {
    return {
      width: viewport.width,
      height: viewport.height,
      offsetLeft: viewport.offsetLeft || 0,
      offsetTop: viewport.offsetTop || 0
    };
  }

  return {
    width: window.innerWidth,
    height: window.innerHeight,
    offsetLeft: 0,
    offsetTop: 0
  };
};

const clampWidgetPosition = (
  position = {},
  width = ESTIMATED_WIDGET_WIDTH,
  height = ESTIMATED_WIDGET_HEIGHT
) => {
  const topSafeMargin = getTopSafeMargin();
  const viewport = getViewportMetrics();
  const safeWidth = Math.max(220, Number(width) || ESTIMATED_WIDGET_WIDTH);
  const safeHeight = Math.max(120, Number(height) || ESTIMATED_WIDGET_HEIGHT);
  const minLeft = viewport.offsetLeft + MIN_WIDGET_MARGIN;
  const minTop = viewport.offsetTop + topSafeMargin;
  const maxLeft = Math.max(
    minLeft,
    viewport.offsetLeft + viewport.width - safeWidth - MIN_WIDGET_MARGIN
  );
  const maxTop = Math.max(
    minTop,
    viewport.offsetTop + viewport.height - safeHeight - MIN_WIDGET_MARGIN
  );

  return {
    left: Math.min(
      maxLeft,
      Math.max(
        minLeft,
        Number(position.left) || minLeft
      )
    ),
    top: Math.min(
      maxTop,
      Math.max(
        minTop,
        Number(position.top) || minTop
      )
    )
  };
};

const isInteractiveDragException = target => {
  if (!target || typeof target.closest !== "function") {
    return false;
  }

  return Boolean(
    target.closest("button") ||
    target.closest("a") ||
    target.closest("[role='button']") ||
    target.closest("input") ||
    target.closest("textarea") ||
    target.closest("select")
  );
};

const playCompletedSound = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const context = new AudioContextClass();
    const oscillator = context.createOscillator();
    const gain = context.createGain();

    oscillator.type = "triangle";
    oscillator.frequency.setValueAtTime(880, context.currentTime);
    oscillator.frequency.exponentialRampToValueAtTime(1175, context.currentTime + 0.18);

    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.12, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.28);

    oscillator.connect(gain);
    gain.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.3);
    oscillator.onended = () => {
      if (typeof context.close === "function") {
        context.close().catch(() => undefined);
      }
    };
  } catch (_error) {
    // ignore
  }
};

const TaskWidgetCard = ({
  task,
  index,
  position,
  onPositionChange,
  onPause,
  onResume,
  onComplete,
  onOpenTasks,
  onResetPosition
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const widgetRef = useRef(null);
  const pointerSupported = typeof window !== "undefined" && "PointerEvent" in window && !isMobileViewport();
  const [visualPosition, setVisualPosition] = useState(position);
  const dragRef = useRef({
    active: false,
    source: null,
    pointerId: null,
    startX: 0,
    startY: 0,
    lastX: 0,
    lastY: 0,
    pointerOffsetX: 0,
    pointerOffsetY: 0,
    startLeft: position.left,
    startTop: position.top,
    pendingPosition: {
      left: position.left,
      top: position.top
    }
  });

  const applyWidgetPosition = useCallback(nextPosition => {
    if (!widgetRef.current || !nextPosition) {
      return;
    }

    widgetRef.current.style.left = `${nextPosition.left}px`;
    widgetRef.current.style.top = `${nextPosition.top}px`;
  }, []);

  useEffect(() => {
    if (dragRef.current.active) {
      return;
    }

    setVisualPosition(position);
    dragRef.current.startLeft = position.left;
    dragRef.current.startTop = position.top;
    dragRef.current.pendingPosition = {
      left: position.left,
      top: position.top
    };
  }, [applyWidgetPosition, position.left, position.top]);

  useEffect(() => {
    const syncToActualWidgetBounds = () => {
      if (dragRef.current.active) {
        return;
      }

      if (!widgetRef.current) {
        return;
      }

      const rect = widgetRef.current.getBoundingClientRect();
      const normalizedPosition = clampWidgetPosition(
        position,
        rect.width || widgetRef.current.offsetWidth || ESTIMATED_WIDGET_WIDTH,
        rect.height || widgetRef.current.offsetHeight || ESTIMATED_WIDGET_HEIGHT
      );

      if (
        normalizedPosition.left !== position.left ||
        normalizedPosition.top !== position.top
      ) {
        setVisualPosition(normalizedPosition);
        onPositionChange(task.id, normalizedPosition);
      } else {
        setVisualPosition(normalizedPosition);
      }
    };

    syncToActualWidgetBounds();
  }, [applyWidgetPosition, onPositionChange, position, task.id]);

  useEffect(() => {
    const handleMove = event => {
      if (!dragRef.current.active) return;
      const isTouchEvent = "touches" in event;
      const isMouseEvent = event.type === "mousemove";

      if (dragRef.current.source === "touch" && !isTouchEvent) {
        return;
      }

      if (dragRef.current.source === "mouse" && !isMouseEvent) {
        return;
      }

      if (dragRef.current.pointerId !== null && event.pointerId !== undefined && event.pointerId !== dragRef.current.pointerId) {
        return;
      }
      if ("touches" in event) {
        event.preventDefault();
      }
      const point = "touches" in event ? event.touches[0] : event;
      const cardWidth = widgetRef.current?.offsetWidth || 290;
      const cardHeight = widgetRef.current?.offsetHeight || 190;
      const baseLeft = dragRef.current.active ? dragRef.current.startLeft : position.left;
      const baseTop = dragRef.current.active ? dragRef.current.startTop : position.top;
      const rawDeltaX = point.clientX - dragRef.current.lastX;
      const rawDeltaY = point.clientY - dragRef.current.lastY;
      const nextPosition = clampWidgetPosition(
        {
          left: baseLeft + rawDeltaX,
          top: baseTop + rawDeltaY
        },
        cardWidth,
        cardHeight
      );
      dragRef.current.startLeft = nextPosition.left;
      dragRef.current.startTop = nextPosition.top;
      dragRef.current.pendingPosition = nextPosition;
      dragRef.current.lastX = point.clientX;
      dragRef.current.lastY = point.clientY;
      setVisualPosition(nextPosition);
    };

    const stopMove = event => {
      const isTouchEvent =
        event?.type === "touchend" || event?.type === "touchcancel";
      const isMouseEvent = event?.type === "mouseup";

      if (dragRef.current.source === "touch" && !isTouchEvent) {
        return;
      }

      if (dragRef.current.source === "mouse" && !isMouseEvent) {
        return;
      }

      if (dragRef.current.pointerId !== null && event?.pointerId !== undefined && event.pointerId !== dragRef.current.pointerId) {
        return;
      }
      const finalPosition = dragRef.current.pendingPosition;
      dragRef.current.active = false;
      dragRef.current.source = null;
      dragRef.current.pointerId = null;
      if (finalPosition) {
        onPositionChange(task.id, finalPosition);
      }
    };

    if (pointerSupported) {
      window.addEventListener("pointermove", handleMove);
      window.addEventListener("pointerup", stopMove);
      window.addEventListener("pointercancel", stopMove);
    } else {
      window.addEventListener("mousemove", handleMove);
      window.addEventListener("mouseup", stopMove);
      window.addEventListener("touchmove", handleMove, { passive: false });
      window.addEventListener("touchend", stopMove);
    }

    return () => {
      if (pointerSupported) {
        window.removeEventListener("pointermove", handleMove);
        window.removeEventListener("pointerup", stopMove);
        window.removeEventListener("pointercancel", stopMove);
      } else {
        window.removeEventListener("mousemove", handleMove);
        window.removeEventListener("mouseup", stopMove);
        window.removeEventListener("touchmove", handleMove);
        window.removeEventListener("touchend", stopMove);
      }
    };
  }, [applyWidgetPosition, onPositionChange, pointerSupported, position.left, position.top, task.id]);

  const startDrag = event => {
    if (isInteractiveDragException(event.target)) {
      return;
    }
    if (dragRef.current.active) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    const point = "touches" in event ? event.touches[0] : event;
    const rect = widgetRef.current?.getBoundingClientRect();
    const viewport = getViewportMetrics();
    const currentLeft = rect?.left ?? position.left ?? (viewport.offsetLeft + MIN_WIDGET_MARGIN);
    const currentTop = rect?.top ?? position.top ?? (viewport.offsetTop + getTopSafeMargin());
    const pointerOffsetX = rect ? point.clientX - rect.left : 0;
    const pointerOffsetY = rect ? point.clientY - rect.top : 0;
    if (event.currentTarget?.setPointerCapture && event.pointerId !== undefined) {
      try {
        event.currentTarget.setPointerCapture(event.pointerId);
      } catch (_error) {
        // ignore capture errors
      }
    }
    dragRef.current = {
      active: true,
      source: pointerSupported ? "pointer" : ("touches" in event ? "touch" : "mouse"),
      pointerId: event.pointerId ?? null,
      startX: point.clientX,
      startY: point.clientY,
      lastX: point.clientX,
      lastY: point.clientY,
      pointerOffsetX,
      pointerOffsetY,
      startLeft: currentLeft,
      startTop: currentTop,
      pendingPosition: {
        left: currentLeft,
        top: currentTop
      }
    };
  };

  const statusColor = task.status === "paused" ? "#f59e0b" : "#16a34a";

  return (
    <Paper
      ref={widgetRef}
      elevation={0}
      className={classes.widget}
      style={{
        left: visualPosition.left,
        top: visualPosition.top,
        borderColor: alpha(statusColor, 0.35)
      }}
    >
      <div className={classes.header}>
        <div
          className={classes.dragArea}
          onPointerDown={pointerSupported ? startDrag : undefined}
          onMouseDown={!pointerSupported ? startDrag : undefined}
          onTouchStart={!pointerSupported ? startDrag : undefined}
        >
          <DragIndicatorIcon style={{ color: theme.palette.text.secondary }} />
          <div className={classes.titleWrap}>
            <Typography variant="subtitle2" className={classes.title}>
              {task.title}
            </Typography>
            <Typography variant="caption" color="textSecondary" className={classes.subtitle}>
              Tarea #{task.id} {task.contact?.name ? `· ${task.contact.name}` : ""}
            </Typography>
          </div>
        </div>
        <Tooltip title="Abrir tareas">
          <IconButton size="small" onClick={onOpenTasks}>
            <OpenInNewIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Recentrar widget">
          <IconButton size="small" onClick={() => onResetPosition(task.id, index)}>
            <CenterFocusStrongIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </div>

      <div className={classes.timerBox}>
        <Typography variant="caption" color="textSecondary">
          {task.status === "in_progress" ? "Tiempo corriendo" : "Tiempo acumulado"}
        </Typography>
        <Typography variant="h6" className={classes.timerValue}>
          {formatDuration(task.elapsedSecondsLive || task.elapsedSeconds || 0)}
        </Typography>
      </div>

      <div className={classes.metaRow}>
        <Chip
          size="small"
          label={task.status === "in_progress" ? "En curso" : "Pausada"}
          style={{
            backgroundColor: alpha(statusColor, 0.14),
            color: statusColor,
            fontWeight: 700
          }}
        />
        {task.assignedUser?.name && (
          <Chip size="small" variant="outlined" label={task.assignedUser.name} />
        )}
      </div>

      <div className={classes.footerActions}>
        {task.status === "in_progress" ? (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<PauseIcon />}
            className={classes.actionButton}
            onClick={() => onPause(task)}
          >
            Pausar
          </Button>
        ) : (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<PlayArrowIcon />}
            className={classes.actionButton}
            onClick={() => onResume(task)}
          >
            Retomar
          </Button>
        )}
        <Button
          size="small"
          variant="contained"
          startIcon={<CheckCircleIcon />}
          className={`${classes.actionButton} ${classes.primaryDone}`}
          onClick={() => onComplete(task)}
        >
          Terminar
        </Button>
      </div>
    </Paper>
  );
};

export default function OperationalTaskFloatingWidgets() {
  const classes = useStyles();
  const history = useHistory();
  const { user, isAuth } = useContext(AuthContext);
  const [records, setRecords] = useState([]);
  const [nowTick, setNowTick] = useState(Date.now());
  const [positions, setPositions] = useState(() => readStoredPositions());
  const previousVisibleIdsRef = useRef([]);

  const refreshTasks = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data } = await api.get("/operational-tasks", {
        params: {
          includeCompleted: "true",
          assignedUserId: user.id
        }
      });
      setRecords(Array.isArray(data) ? data : []);
    } catch (error) {
      const status = error?.response?.status;
      if (status === 401 || status === 403) {
        return;
      }
      if (window.location.pathname !== "/login") {
        console.error("No fue posible cargar widgets de tareas", error);
      }
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isAuth || !user?.id) {
      return undefined;
    }

    refreshTasks();

    const interval = setInterval(refreshTasks, 12000);
    const syncHandler = () => refreshTasks();
    window.addEventListener(UPDATE_EVENT, syncHandler);

    return () => {
      clearInterval(interval);
      window.removeEventListener(UPDATE_EVENT, syncHandler);
    };
  }, [isAuth, refreshTasks, user?.id]);

  useEffect(() => {
    const interval = setInterval(() => setNowTick(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const visibleTasks = useMemo(() => {
    const liveTasks = records
      .filter(task => ["in_progress", "paused"].includes(String(task.status)))
      .map(task => {
        const liveSeconds = task.status === "in_progress" && task.lastResumedAt
          ? Math.max(0, Math.floor((nowTick - new Date(task.lastResumedAt).getTime()) / 1000))
          : 0;
        return {
          ...task,
          elapsedSecondsLive: Number(task.accumulatedSeconds || 0) + liveSeconds
        };
      })
      .sort((a, b) => {
        const statusOrder = a.status === b.status ? 0 : a.status === "in_progress" ? -1 : 1;
        if (statusOrder !== 0) return statusOrder;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });

    return liveTasks;
  }, [nowTick, records]);

  const visibleTasksLayoutKey = useMemo(
    () => visibleTasks.map(task => `${task.id}:${task.status}`).join("|"),
    [visibleTasks]
  );

  useEffect(() => {
    persistStoredPositions(positions);
  }, [positions]);

  useEffect(() => {
    const normalizeVisibleWidgetPositions = () => {
      setPositions(prev => {
        let hasChanges = false;
        const next = { ...prev };

        visibleTasks.forEach((task, index) => {
          const defaultPosition = computeDefaultPosition(index);
          const storedPosition = next[task.id];
          const basePosition = storedPosition || defaultPosition;
          const normalizedPosition = clampWidgetPosition(basePosition);

          if (
            !next[task.id] ||
            normalizedPosition.left !== basePosition.left ||
            normalizedPosition.top !== basePosition.top
          ) {
            next[task.id] = normalizedPosition;
            hasChanges = true;
          }
        });

        return hasChanges ? next : prev;
      });
    };

    normalizeVisibleWidgetPositions();
    window.addEventListener("resize", normalizeVisibleWidgetPositions);

    return () => {
      window.removeEventListener("resize", normalizeVisibleWidgetPositions);
    };
  }, [visibleTasksLayoutKey]);

  useEffect(() => {
    const currentIds = visibleTasks.map(task => task.id);
    const previousIds = previousVisibleIdsRef.current;
    const removedIds = previousIds.filter(id => !currentIds.includes(id));

    if (removedIds.length > 0) {
      persistStoredPositions(
        Object.fromEntries(
          Object.entries(readStoredPositions()).filter(([key]) => !removedIds.includes(Number(key)))
        )
      );
      setPositions(readStoredPositions());
    }

    previousVisibleIdsRef.current = currentIds;
  }, [visibleTasks]);

  const handlePositionChange = useCallback((taskId, nextPosition) => {
    setPositions(prev => ({
      ...prev,
      [taskId]: nextPosition
    }));
  }, []);

  const handleResetPosition = useCallback((taskId, index) => {
    setPositions(prev => ({
      ...prev,
      [taskId]: clampWidgetPosition(computeDefaultPosition(index))
    }));
  }, []);

  const notifySync = () => {
    window.dispatchEvent(new Event(UPDATE_EVENT));
  };

  const handleAction = async (task, action, payload = {}, options = {}) => {
    try {
      await api.post(`/operational-tasks/${task.id}/${action}`, payload);
      if (options.playSound) {
        playCompletedSound();
      }
      notifySync();
      refreshTasks();
    } catch (error) {
      toastError(error);
    }
  };

  const handlePause = task => {
    const note = window.prompt("Escriba la justificacion de la pausa:", "");

    if (note === null) {
      return;
    }

    const normalizedNote = String(note || "").trim();

    if (!normalizedNote) {
      toast.warning("Debe escribir una justificacion para pausar la tarea.");
      return;
    }

    handleAction(task, "pause", {
      reason: "other",
      note: normalizedNote
    });
  };

  const handleResume = task => {
    handleAction(task, "resume");
  };

  const handleComplete = task => {
    handleAction(
      task,
      "complete",
      { note: "Terminada desde widget flotante" },
      { playSound: true }
    );
  };

  const openTasks = () => {
    history.push("/todolist");
  };

  if (!isAuth || !user?.id || window.location.pathname === "/todolist" || visibleTasks.length === 0) {
    return null;
  }

  if (typeof document === "undefined" || !document.body) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className={classes.container}>
      {visibleTasks.map((task, index) => (
        <TaskWidgetCard
          key={task.id}
          task={task}
          index={index}
          position={clampWidgetPosition(positions[task.id] || computeDefaultPosition(index))}
          onPositionChange={handlePositionChange}
          onPause={handlePause}
          onResume={handleResume}
          onComplete={handleComplete}
          onOpenTasks={openTasks}
          onResetPosition={handleResetPosition}
        />
      ))}
    </div>,
    document.body
  );
}
